import React, { useState } from 'react';
import { X, Bookmark, Globe, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { libraryApi } from '../api/client';
import type { Component } from '../schemas/validation';

interface SaveToLibraryModalProps {
  isOpen: boolean;
  component: Component | null;
  pageId: string;
  siteId: string;
  onClose: () => void;
  onSuccess?: () => void;
  isAdmin?: boolean;
}

export const SaveToLibraryModal: React.FC<SaveToLibraryModalProps> = ({
  isOpen,
  component,
  pageId,
  siteId,
  onClose,
  onSuccess,
  isAdmin = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: '',
    isPublic: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Reset form when modal opens with a new component
  React.useEffect(() => {
    if (isOpen && component) {
      const defaultName = `${component.type.charAt(0).toUpperCase() + component.type.slice(1)} Component`;
      setFormData({
        name: defaultName,
        description: '',
        tags: component.type,
        isPublic: false,
      });
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, component]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!component) return;

    // Validation
    if (!formData.name.trim()) {
      setError('Component name is required');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const saveData = {
        componentId: component.id,
        siteId: siteId,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        tags: formData.tags.trim() || undefined,
        isPublic: formData.isPublic,
      };

      console.log('Saving component to library with data:', saveData);
      const result = await libraryApi.saveComponent(pageId, saveData);
      console.log('Save component result:', result);

      setSuccess(true);

      // Close modal after a brief delay to show success
      setTimeout(() => {
        onClose();
        onSuccess?.();
      }, 1500);
    } catch (err: any) {
      console.error('Failed to save component to library:', err);
      setError(err.message || 'Failed to save component to library');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !component) return null;

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 border border-gray-100">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Component Saved!</h3>
            <p className="text-gray-600">
              "{formData.name}" has been added to your component library.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-lg">
              <Bookmark className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Save to Library</h2>
              <p className="text-sm text-gray-500">
                Reuse this {component.type} component across pages
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {/* Component Name */}
          <div>
            <label htmlFor="componentName" className="block text-sm font-medium text-gray-700 mb-2">
              Component Name *
            </label>
            <input
              id="componentName"
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="e.g., Main Navigation Bar"
              required
              disabled={isLoading}
            />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="componentDescription"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Description
            </label>
            <textarea
              id="componentDescription"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Describe what this component does and when to use it..."
              disabled={isLoading}
            />
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="componentTags" className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <input
              id="componentTags"
              type="text"
              value={formData.tags}
              onChange={e => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="navigation, header, responsive"
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Separate tags with commas to help organize and find components
            </p>
          </div>

          {/* Visibility (only show for admins) */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Visibility</label>
              <div className="space-y-3">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="visibility"
                    checked={!formData.isPublic}
                    onChange={() => setFormData({ ...formData, isPublic: false })}
                    className="mt-0.5 text-purple-600 focus:ring-purple-500"
                    disabled={isLoading}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Lock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-900">Private</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Only you can see and use this component
                    </p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="visibility"
                    checked={formData.isPublic}
                    onChange={() => setFormData({ ...formData, isPublic: true })}
                    className="mt-0.5 text-purple-600 focus:ring-purple-500"
                    disabled={isLoading}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Globe className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-gray-900">Public</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Other users can discover and use this component
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isLoading || !formData.name.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Bookmark className="w-4 h-4" />
                <span>Save to Library</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
